/* components/patterns/DataTable.jsx — ЕДИНЫЙ адаптивный примитив таблицы.
   // заменяет: Table + все кастомные grid-таблицы модулей.
   Пропсы: columns[{key,label,render,align,hideBelow,width}], rows[], rowKey,
           compact, zebra, onRowClick, loading, empty, cardTitle.
   На мобильной полосе (bp xs+sm) → режим карточек (строка = карточка пар label:value).
   Никаких !important, никакого скрытия данных — в картах видны ВСЕ колонки.
   ХЕНДОФФ → frontend/components/common/DataTable.jsx. */

import React from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '../ui/Icon.jsx';
import { Popover, PopoverItem } from '../ui/Popover.jsx';
import { hooks } from '../../hooks/index.jsx';
import { getOverlayRoot, overlay } from '../../lib/overlay.jsx';

  const { useState } = React;
  const { useBreakpoint } = hooks;
  const { useFocusTrap } = overlay;

  function cell(col, row, i) {
    return col.render ? col.render(row, i) : (row[col.key] ?? '—');
  }

  function DataTable({ columns = [], rows = [], rowKey, compact = false, zebra = false,
                       onRowClick, loading = false, empty, cardTitle, className }) {
    const { bp } = useBreakpoint();
    // карточный режим на мобильной полосе xs+sm — единый контракт с IngredientsTable/
    // PositionsTable/DeltaTable и докстрингом «строка = карточка»
    const cardMode = bp === 'xs' || bp === 'sm';
    const keyOf = (row, i) => (rowKey ? (typeof rowKey === 'function' ? rowKey(row) : row[rowKey]) : i);

    if (loading) {
      return (
        <div className={'data-table data-table--loading' + (className ? ' ' + className : '')} aria-busy="true">
          {Array.from({ length: 6 }).map((_, r) => (
            <div className="data-table__skel-row" key={r}>
              {columns.map((c, i) => <span className="skeleton data-table__skel-cell" key={i} style={{ width: i === 0 ? '40%' : '70%' }} />)}
            </div>
          ))}
        </div>
      );
    }
    if (!rows.length) {
      return <div className="data-table__empty">{empty || <span className="text-mute">Нет записей</span>}</div>;
    }

    /* ── Карточный режим (xs) ── */
    if (cardMode) {
      return (
        <div className={'dt-cards' + (className ? ' ' + className : '')}>
          {rows.map((row, i) => {
            const title = cardTitle ? cardTitle(row) : cell(columns[0], row, i);
            const rest = cardTitle ? columns : columns.slice(1);
            return (
              <div className={'dt-card' + (onRowClick ? ' is-clickable' : '')} key={keyOf(row, i)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                role={onRowClick ? 'button' : undefined} tabIndex={onRowClick ? 0 : undefined}>
                <div className="dt-card__title t-label">{title}</div>
                <dl className="dt-card__list">
                  {rest.map((c) => (
                    <div className="dt-card__pair" key={c.key}>
                      <dt className="t-caption">{c.label}</dt>
                      <dd className={c.align === 'right' ? 'num' : ''}>{cell(c, row, i)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })}
        </div>
      );
    }

    /* ── Табличный режим (md+) ── */
    return (
      <div className={'data-table-wrap' + (className ? ' ' + className : '')}>
        <table className={'data-table' + (compact ? ' data-table--compact' : '') + (zebra ? ' data-table--zebra' : '')}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.align === 'right' ? 'is-right' : ''} style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={keyOf(row, i)} className={onRowClick ? 'is-clickable' : ''}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter') onRowClick(row); } : undefined}>
                {columns.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'is-right ' + (c.mono !== false ? 'num' : '') : ''}>
                    {cell(c, row, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* SortMenu — меню сортировки. На xs → bottom-sheet (.select-sheet, как пикеры),
     на остальных ширинах → Popover bottom-end (// объединяет sort-меню модулей). */
  function SortMobileSheet({ options, value, onChange, label, onClose }) {
    const sheetRef = React.useRef(null);
    useFocusTrap(sheetRef, { active: true, onEscape: onClose });
    return ReactDOM.createPortal(
      <div className="overlay overlay--sheet" style={{ zIndex: 'var(--z-popover)' }}>
        <div className="overlay__backdrop" onClick={onClose} />
        <div className="select-sheet" ref={sheetRef} role="dialog" aria-modal="true" aria-label={label}>
          <div className="select-sheet__head">
            <span className="t-h4 grow">{label}</span>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Закрыть"><Icon name="close" size={18} /></button>
          </div>
          <div className="select-pop__list" role="listbox" aria-label={label}>
            {options.map((o) => (
              <button type="button" key={o.value} role="option" aria-selected={o.value === value}
                className={'select-pop__opt' + (o.value === value ? ' is-selected' : '')}
                onClick={() => { onChange && onChange(o.value); onClose(); }}>
                <span className="grow"><span className="select-pop__opt-label">{o.label}</span></span>
                {o.value === value && <Icon name="check" size={16} className="select-pop__tick" />}
              </button>
            ))}
          </div>
        </div>
      </div>,
      getOverlayRoot()
    );
  }

  function SortMenu({ options = [], value, onChange, label = 'Сортировка' }) {
    const { bp } = useBreakpoint();
    const [open, setOpen] = useState(false);
    const cur = options.find((o) => o.value === value);
    const triggerBtn = (extra) => (
      <button type="button" className="btn btn--secondary btn--md" {...extra}>
        <Icon name="sort" size={16} /><span className="btn__label">{cur ? cur.label : label}</span><Icon name="chevron-down" size={16} />
      </button>
    );
    if (bp === 'xs') {
      return (
        <>
          {triggerBtn({ onClick: () => setOpen(true), 'aria-haspopup': 'listbox', 'aria-expanded': open })}
          {open && <SortMobileSheet options={options} value={value} onChange={onChange} label={label} onClose={() => setOpen(false)} />}
        </>
      );
    }
    return (
      <Popover placement="bottom-end" trigger={triggerBtn()}>
        {({ close }) => (
          <div className="stack stack-2">
            {options.map((o) => (
              <PopoverItem key={o.value} icon={o.value === value ? 'check' : undefined} close={close} onClick={() => onChange && onChange(o.value)}>{o.label}</PopoverItem>
            ))}
          </div>
        )}
      </Popover>
    );
  }

export { DataTable };
export { SortMenu };
